// Documentos impresos de tesorería con el formato de Random (pedido de Rodrigo, 2026-10-14): cartola
// de cliente y de proveedor, comprobante de ingreso (cobro) y de egreso (pago a proveedor). Cada uno
// junta lo que necesita (ficha de la entidad, documentos con saldo) y recién entonces imprime.
import { useEffect, useRef } from 'react';
import { guardarArchivo } from '../api/cliente';
import { useComunas, useFichaCliente } from '../api/consultas';
import { Imprimir } from '../comun/Imprimir';
import { pdfDeReporte } from '../comun/pdfReporte';
import { Reporte } from '../logistica/ImpresosOd';
import { ddmmaaaa, type LineaReporte } from '../logistica/reporteRandom';
import { useSesion } from '../sesion/SesionContexto';
import { detallePago, useCartola, useDocumentosCxc, useRecibos } from './datos';
import { pesos } from '../comun/formato';
import {
  siglaCompra,
  useDocumentosCxp,
  usePagos,
  type DocumentoProveedor,
  type PagoProveedor,
} from './datosPagos';
import {
  textoCartola,
  textoCartolaVendedor,
  textoComprobanteEgreso,
  textoComprobanteIngreso,
  type CuentaImpresa,
  type PendienteImpreso,
  type EmpresaImpresa,
  type EntidadImpresa,
  type VendedorImpreso,
} from './reporteCuenta';

/** Tipo de documento con el código de Random (FCV, BLV, NCV…). */
const TD_VENTA: Record<number, string> = {
  33: 'FCV',
  34: 'FEV',
  39: 'BLV',
  41: 'BEV',
  56: 'NDV',
  61: 'NCV',
};

/** Lo que se le debe al proveedor, documento por documento (las NC no deben). */
const pendienteDeCompra = (d: DocumentoProveedor): PendienteImpreso => ({
  td: siglaCompra(d),
  numero: d.folio ?? '—',
  emision: d.fechaEmision,
  vence: d.fechaVencimiento,
  diasVencido: d.diasVencido,
  valor: d.montoTotal,
  saldo: d.saldo,
});

function useEmpresaImpresa(): EmpresaImpresa {
  const { empresa } = useSesion();
  return { razonSocial: empresa!.razon_social, rut: empresa!.rut, dv: empresa!.dv };
}

/** RUT, dirección, fono y línea de crédito de la entidad, desde su ficha. */
export function useEntidadImpresa(empresaId: string, entidadId: string | null, nombre: string) {
  const ficha = useFichaCliente(empresaId, entidadId);
  const comunas = useComunas();
  if (!ficha.data)
    return { listo: ficha.isError, entidad: { rut: null, nombre } as EntidadImpresa };
  const f = ficha.data;
  const activas = f.direcciones.filter((d) => d.activa);
  const dir =
    activas.find((d) => d.es_tributaria) ?? activas.find((d) => d.es_principal) ?? activas[0];
  const comuna = dir?.comuna_codigo
    ? (comunas.data?.find((c) => c.codigo === dir.comuna_codigo)?.nombre ?? '')
    : '';
  const fono =
    f.medios.find((m) => m.tipo === 'telefono' && m.activo !== false && m.principal) ??
    f.medios.find((m) => m.tipo === 'telefono' && m.activo !== false);
  const rel = f.empresas.find((e) => e.empresa_id === empresaId);
  const e = f.entidad;
  return {
    listo: !comunas.isLoading,
    entidad: {
      rut: e.rut ? `${Number(e.rut).toLocaleString('es-CL')}-${e.dv ?? ''}` : null,
      nombre: e.razon_social,
      direccion: dir
        ? [`${dir.calle} ${dir.numero ?? ''}`.trim(), comuna].filter(Boolean).join(', ')
        : null,
      fono: fono?.valor ?? null,
      lineaCredito: rel?.linea_credito ?? null,
    } satisfies EntidadImpresa,
  };
}

// ---------------------------------------------------------------- cliente

interface PropsCartolaCliente {
  clienteId: string;
  nombre: string;
  desde?: string | undefined;
  hasta?: string | undefined;
  alTerminar: () => void;
}

/** El texto de la cartola del cliente, recién cuando llegaron la cuenta y la ficha. */
function useTextoCartolaCliente(
  { clienteId, nombre, desde, hasta }: PropsCartolaCliente,
  paraCliente = false,
): LineaReporte[] | null {
  const { empresa } = useSesion();
  const emp = useEmpresaImpresa();
  const cartola = useCartola(empresa!.id, clienteId, {
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
  });
  const ent = useEntidadImpresa(empresa!.id, clienteId, nombre);
  if (!cartola.data || !ent.listo) return null;
  return textoCartola({
    empresa: emp,
    entidad: ent.entidad,
    saldoAFavor: cartola.data.saldoAFavor,
    pendientes: cartola.data.pendientes ?? [],
    paraCliente,
  });
}

export function ImprimirCartolaCliente(p: PropsCartolaCliente) {
  const lineas = useTextoCartolaCliente(p);
  if (!lineas) return null;
  return (
    <Imprimir alTerminar={p.alTerminar} horizontal>
      <Reporte horizontal lineas={lineas} />
    </Imprimir>
  );
}

/**
 * La misma cartola como archivo PDF, para mandársela al cliente por WhatsApp (Nairobis, 2026-11-02):
 * se descarga sin pasar por la impresora, porque en el PC del mesón imprimir sale directo al papel.
 */
export function DescargarCartolaPdf(p: PropsCartolaCliente) {
  const lineas = useTextoCartolaCliente(p, true);
  const hecho = useRef(false);
  const { nombre, alTerminar } = p;
  useEffect(() => {
    if (!lineas || hecho.current) return;
    hecho.current = true;
    const hoy = ddmmaaaa(new Date().toLocaleDateString('sv-SE')).replace(/\//g, '-');
    const archivo = `Cartola ${nombre.replace(/[\\/:*?"<>|]+/g, ' ').trim()} ${hoy}.pdf`;
    guardarArchivo(
      pdfDeReporte(lineas, { titulo: `Cartola ${nombre}`, horizontal: true }),
      archivo,
    );
    alTerminar();
  }, [lineas, nombre, alTerminar]);
  return null;
}

/** La cartola general por vendedor: lo que se ve en pantalla (con su filtro), ya cargado. */
export function ImprimirCartolaVendedor({
  titulo,
  vendedores,
  clientes,
  detalle,
  alTerminar,
}: {
  titulo: string;
  vendedores: VendedorImpreso[];
  clientes: CuentaImpresa[];
  detalle: boolean;
  alTerminar: () => void;
}) {
  const emp = useEmpresaImpresa();
  return (
    <Imprimir alTerminar={alTerminar} horizontal>
      <Reporte
        horizontal
        lineas={textoCartolaVendedor({
          empresa: emp,
          titulo,
          // Con todos los vendedores, solo los que tienen clientes en la lista.
          vendedores: vendedores.length > 1 ? vendedores.filter((v) => v.clientes > 0) : vendedores,
          clientes,
          detalle,
        })}
      />
    </Imprimir>
  );
}

export function ImprimirRecibo({
  reciboId,
  clienteId,
  nombre,
  alTerminar,
}: {
  reciboId: string;
  clienteId: string;
  nombre: string;
  alTerminar: () => void;
}) {
  const { empresa } = useSesion();
  const emp = useEmpresaImpresa();
  const recibos = useRecibos(empresa!.id, { clienteEntidadId: clienteId });
  const docs = useDocumentosCxc(empresa!.id, { clienteEntidadId: clienteId });
  const ent = useEntidadImpresa(empresa!.id, clienteId, nombre);
  const r = recibos.data?.find((x) => x.reciboId === reciboId);
  if (!r || !docs.data || !ent.listo) return null;
  const porId = new Map(docs.data.map((d) => [d.documentoId, d]));
  return (
    <Imprimir alTerminar={alTerminar}>
      <Reporte
        lineas={textoComprobanteIngreso({
          empresa: emp,
          entidad: ent.entidad,
          numero: r.numeroVisible,
          fecha: r.creadoEn,
          estado: r.estado,
          medios: r.medios.map((m) => ({
            medio: m.medio,
            numero: m.numeroDocumento ?? m.autorizacion,
            banco: m.banco,
            fecha: m.fechaDocumento,
            referencia: m.referencia,
            monto: m.monto,
          })),
          aplicaciones: r.aplicaciones
            .filter((a) => a.tipo === 'APLICACION' && !a.reversada)
            .map((a) => {
              const d = porId.get(a.documentoId);
              return {
                td: TD_VENTA[a.tipoDte ?? 0] ?? 'DOC',
                folio: a.folio,
                emision: d?.fechaEmision ?? null,
                vence: d?.fechaVencimiento ?? null,
                valor: d?.montoTotal ?? null,
                abono: a.monto,
                saldoHoy: d?.saldo ?? null,
                pagadoCon:
                  (a.medios ?? []).length > 0
                    ? a
                        .medios!.map((x) => {
                          const m = r.medios.find((y) => y.linea === x.linea) ?? { medio: x.medio };
                          return `${detallePago(m)} ${pesos(x.monto)}`;
                        })
                        .join(' + ')
                    : null,
              };
            }),
          total: r.total,
          aplicado: r.aplicado,
          saldoAFavor: r.saldoAFavor,
          observacion: r.observacion,
        })}
      />
    </Imprimir>
  );
}

// ---------------------------------------------------------------- proveedor

export function ImprimirCartolaProveedor({
  proveedorId,
  nombre,
  alTerminar,
}: {
  proveedorId: string;
  nombre: string;
  /** La cartola impresa es la deuda de hoy (como Random): el período no la cambia. */
  desde?: string | undefined;
  hasta?: string | undefined;
  alTerminar: () => void;
}) {
  const { empresa } = useSesion();
  const emp = useEmpresaImpresa();
  const docs = useDocumentosCxp(empresa!.id, { proveedorEntidadId: proveedorId });
  const ent = useEntidadImpresa(empresa!.id, proveedorId, nombre);
  if (!docs.data || !ent.listo) return null;
  return (
    <Imprimir alTerminar={alTerminar} horizontal>
      <Reporte
        horizontal
        lineas={textoCartola({
          empresa: emp,
          entidad: ent.entidad,
          proveedor: true,
          pendientes: docs.data
            .filter((d) => d.clase !== 'NOTA_CREDITO' && Number(d.saldo) !== 0)
            .map(pendienteDeCompra),
        })}
      />
    </Imprimir>
  );
}

export function ImprimirComprobanteEgreso({
  pago: p,
  nombre,
  alTerminar,
}: {
  pago: PagoProveedor;
  nombre: string;
  alTerminar: () => void;
}) {
  const { empresa } = useSesion();
  const emp = useEmpresaImpresa();
  const docs = useDocumentosCxp(empresa!.id, { proveedorEntidadId: p.proveedorEntidadId });
  const ent = useEntidadImpresa(empresa!.id, p.proveedorEntidadId, nombre);
  if (!docs.data || !ent.listo) return null;
  const porId = new Map(docs.data.map((d) => [d.documentoId, d]));
  return (
    <Imprimir alTerminar={alTerminar}>
      <Reporte
        lineas={textoComprobanteEgreso({
          empresa: emp,
          entidad: ent.entidad,
          numero: p.numero,
          estado: p.estado,
          fechaProgramada: p.fechaProgramada,
          pagadoEn: p.pagadoEn,
          medio: {
            medio: p.medio,
            numero: p.numeroDocumento,
            banco: p.banco,
            fecha: p.fechaProgramada,
            referencia: p.comprobante,
            monto: p.total,
          },
          comprobante: p.comprobante,
          aplicaciones: p.aplicaciones
            .filter((a) => a.tipo === 'APLICACION' && !a.reversada)
            .map((a) => {
              const d = porId.get(a.documentoId);
              return {
                td: d ? siglaCompra(d) : 'FCC',
                folio: a.folio,
                emision: d?.fechaEmision ?? null,
                vence: d?.fechaVencimiento ?? null,
                valor: d?.montoTotal ?? null,
                abono: a.monto,
                saldoHoy: d?.saldo ?? null,
              };
            }),
          total: p.total,
          aplicado: p.aplicado,
          anticipo: p.anticipo,
          observacion: p.observacion,
          cuentaOrigen: p.cuentaOrigen ?? null,
          cuentaDestino: p.cuentaDestino ?? null,
          notasCredito: p.notasCredito ?? [],
        })}
      />
    </Imprimir>
  );
}

/** El comprobante de egreso de un pago recién solicitado (se busca por su id). */
export function ImprimirPagoPorId({
  pagoId,
  proveedorId,
  nombre,
  alTerminar,
}: {
  pagoId: string;
  proveedorId: string;
  nombre: string;
  alTerminar: () => void;
}) {
  const { empresa } = useSesion();
  const pagos = usePagos(empresa!.id, { proveedorEntidadId: proveedorId });
  const p = pagos.data?.find((x) => x.pagoId === pagoId);
  return p ? <ImprimirComprobanteEgreso pago={p} nombre={nombre} alTerminar={alTerminar} /> : null;
}
